import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  FileUp,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  DOCUMENT_TYPE_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  IDENTIFICATION_FORMATS,
  IDENTIFICATION_TYPES,
  INCOME_BANDS,
  ISLAMIC_BANKS,
  NIGERIAN_BANKS,
  NIGERIAN_STATES,
  normaliseIdentificationNumber,
  validateAccountNumber,
  validateBvn,
  validateIdentificationNumber,
  formatLongDate,
} from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '@/components/ui';
import { PhoneInput } from '@/components/PhoneInput';
import { StatusBadge } from '@/components/StatusBadge';
import { KYC_STATUS } from '@/lib/status';

interface KycDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileSize: number;
  status: string;
  uploadedAt: string;
  rejectionReason: string | null;
}

interface KycProfile {
  status: string;
  fullLegalName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  residentialAddress: string | null;
  city: string | null;
  state: string | null;
  lga: string | null;
  employmentStatus: string | null;
  organisation: string | null;
  jobTitle: string | null;
  monthlyIncomeBand: string | null;
  nextOfKin: {
    fullName: string;
    relationship: string;
    phone: string;
    email: string | null;
    address: string;
  } | null;
  identificationType: string | null;
  identificationNumber: string | null;
  rejectionReason: string | null;
  fieldsRequiringCorrection: string[];
  suggestedFullName: string;
  bankDetails: {
    bankName: string;
    bankCode: string | null;
    accountNumber: string;
    accountName: string;
  } | null;
  documents: KycDocument[];
  requiredDocuments: string[];
  missingDocuments: string[];
}

const STEPS = ['Identity', 'Address', 'Employment', 'Next of kin', 'Bank', 'Documents'] as const;

/** Sorted so non-interest banks appear first for a membership that may prefer them. */
const BANKS = [
  ...NIGERIAN_BANKS.filter((b) => (ISLAMIC_BANKS as readonly string[]).includes(b.name)),
  ...NIGERIAN_BANKS.filter((b) => !(ISLAMIC_BANKS as readonly string[]).includes(b.name)),
];

export default function MemberKyc() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [declaration, setDeclaration] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['kyc-me'],
    queryFn: () => api.get<KycProfile>('/kyc/me'),
  });

  const [form, setForm] = useState({
    fullLegalName: '',
    dateOfBirth: '',
    gender: '',
    residentialAddress: '',
    city: '',
    state: '',
    lga: '',
    employmentStatus: '',
    organisation: '',
    jobTitle: '',
    monthlyIncomeBand: '',
    nokFullName: '',
    nokRelationship: '',
    nokPhone: '',
    nokEmail: '',
    nokAddress: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    bvn: '',
    identificationType: '',
    identificationNumber: '',
  });

  // Prefill once the profile arrives, so a member correcting one field does
  // not have to retype the rest.
  useEffect(() => {
    if (!profile) return;
    setForm((current) => ({
      ...current,
      fullLegalName: profile.fullLegalName ?? profile.suggestedFullName ?? '',
      dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.slice(0, 10) : '',
      gender: profile.gender ?? '',
      residentialAddress: profile.residentialAddress ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      lga: profile.lga ?? '',
      employmentStatus: profile.employmentStatus ?? '',
      organisation: profile.organisation ?? '',
      jobTitle: profile.jobTitle ?? '',
      monthlyIncomeBand: profile.monthlyIncomeBand ?? '',
      nokFullName: profile.nextOfKin?.fullName ?? '',
      nokRelationship: profile.nextOfKin?.relationship ?? '',
      nokPhone: profile.nextOfKin?.phone ?? '',
      nokEmail: profile.nextOfKin?.email ?? '',
      nokAddress: profile.nextOfKin?.address ?? '',
      bankName: profile.bankDetails?.bankName ?? '',
      accountNumber: profile.bankDetails?.accountNumber ?? '',
      accountName: profile.bankDetails?.accountName ?? profile.suggestedFullName ?? '',
      identificationType: profile.identificationType ?? '',
      identificationNumber: profile.identificationNumber ?? '',
    }));
  }, [profile]);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const payload = useMemo(
    () => ({
      fullLegalName: form.fullLegalName,
      dateOfBirth: form.dateOfBirth,
      gender: form.gender,
      residentialAddress: form.residentialAddress,
      city: form.city,
      state: form.state,
      lga: form.lga,
      employmentStatus: form.employmentStatus,
      organisation: form.organisation || undefined,
      jobTitle: form.jobTitle || undefined,
      monthlyIncomeBand: form.monthlyIncomeBand || undefined,
      nextOfKin: {
        fullName: form.nokFullName,
        relationship: form.nokRelationship,
        phone: form.nokPhone,
        email: form.nokEmail || undefined,
        address: form.nokAddress,
      },
      bankDetails: {
        bankName: form.bankName,
        bankCode: BANKS.find((b) => b.name === form.bankName)?.code,
        accountNumber: form.accountNumber,
        accountName: form.accountName,
        bvn: form.bvn || undefined,
      },
      identificationType: form.identificationType,
      identificationNumber: form.identificationNumber,
    }),
    [form],
  );

  const saveDraft = useMutation({
    mutationFn: () => api.patch('/kyc/me', payload),
    onSuccess: () => {
      toast.success('Saved.');
      void queryClient.invalidateQueries({ queryKey: ['kyc-me'] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save.'),
  });

  const submit = useMutation({
    mutationFn: () => api.post('/kyc/me/submit', { ...payload, declarationAccepted: true }),
    onSuccess: () => {
      toast.success('Submitted for review.');
      setFieldErrors({});
      void queryClient.invalidateQueries({ queryKey: ['kyc-me'] });
      void queryClient.invalidateQueries({ queryKey: ['member-dashboard'] });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        toast.error(error.message);
        // Jump to the first step containing an error, rather than leaving the
        // member to hunt for it.
        const firstStep = stepForField(Object.keys(error.fieldErrors)[0] ?? '');
        if (firstStep !== null) setStep(firstStep);
      } else {
        toast.error('Something went wrong.');
      }
    },
  });

  if (isLoading || !profile) return <Skeleton className="h-96" />;

  const readOnly = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED'].includes(profile.status);

  if (profile.status === 'APPROVED') {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader
          eyebrow="Verification"
          title="Your verified record"
          status={<StatusBadge map={KYC_STATUS} status={profile.status} className="text-sm" />}
        />

        {/*
          One line, not a card. The page title, the badge beside it and a
          panel headed "KYC approved" were three ways of saying the same
          thing before the member reached anything they came to read.
        */}
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-success/20 bg-success-soft/50 px-4 py-3">
          <BadgeCheck size={18} className="mt-0.5 shrink-0 text-success" />
          <p className="text-sm leading-relaxed text-success-soft-foreground">
            Your identity is verified and you can apply for a loan. To change anything held here,
            contact UNIFEMGA — an approved record cannot be edited from this page.
          </p>
        </div>

        <SummaryCard profile={profile} />
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader
          eyebrow="Verification"
          title="Under review"
          status={<StatusBadge map={KYC_STATUS} status={profile.status} className="text-sm" />}
        />
        <Alert tone="info" title="Your submission is with the verification team">
          You will be notified once it has been reviewed. Your details cannot be edited while a
          review is in progress.
        </Alert>
        <SummaryCard profile={profile} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Verification"
        title="Complete your KYC"
        description="Required before you can apply for a loan."
        status={<StatusBadge map={KYC_STATUS} status={profile.status} className="text-sm" />}
      />

      {profile.rejectionReason && (
        <Alert
          tone={profile.status === 'REJECTED' ? 'danger' : 'warning'}
          title={profile.status === 'REJECTED' ? 'Your submission was rejected' : 'Corrections needed'}
          className="mb-6"
        >
          {profile.rejectionReason}
          {profile.fieldsRequiringCorrection.length > 0 && (
            <ul className="mt-2 list-inside list-disc">
              {profile.fieldsRequiringCorrection.map((field) => (
                <li key={field}>{humanise(field)}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {/* Stepper */}
      <div className="mb-6 flex flex-wrap gap-1.5">
        {STEPS.map((label, index) => (
          <button
            key={label}
            type="button"
            onClick={() => setStep(index)}
            className={cn(
              'flex-1 rounded-lg border px-3 py-2 text-xs font-bold transition-colors',
              index === step
                ? 'border-primary bg-primary text-primary-foreground'
                : isStepComplete(index, form)
                  ? 'border-success/30 bg-success-soft text-success-soft-foreground'
                  : 'border-border bg-background text-foreground-secondary hover:bg-accent',
            )}
          >
            {index !== step && isStepComplete(index, form) && (
              <Check size={12} className="mr-1 inline" />
            )}
            {label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{STEPS[step]}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          {step === 0 && (
            <>
              <Field label="Full legal name" htmlFor="fullLegalName" required error={fieldErrors['fullLegalName']}>
                <Input id="fullLegalName" value={form.fullLegalName} onChange={set('fullLegalName')} />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Date of birth" htmlFor="dob" required error={fieldErrors['dateOfBirth']}>
                  <Input id="dob" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} />
                </Field>
                <Field label="Gender" htmlFor="gender" required error={fieldErrors['gender']}>
                  <Select id="gender" value={form.gender} onChange={set('gender')}>
                    <option value="">Select…</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </Select>
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Identification type" htmlFor="idType" required error={fieldErrors['identificationType']}>
                  <Select id="idType" value={form.identificationType} onChange={set('identificationType')}>
                    <option value="">Select…</option>
                    {IDENTIFICATION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Identification number"
                  htmlFor="idNumber"
                  required
                  // The live check only fires once something has been typed —
                  // an empty field is incomplete, not wrong, and colouring it
                  // red before anyone has touched it is just noise.
                  error={
                    fieldErrors['identificationNumber'] ??
                    (form.identificationNumber.trim()
                      ? (validateIdentificationNumber(
                          form.identificationType,
                          form.identificationNumber,
                        ) ?? undefined)
                      : undefined)
                  }
                  hint={
                    IDENTIFICATION_FORMATS[form.identificationType]
                      ? `${IDENTIFICATION_FORMATS[form.identificationType]!.hint} Stored encrypted — only you and a verification officer can see it.`
                      : 'Stored encrypted. Only you and a verification officer can see it.'
                  }
                >
                  <Input
                    id="idNumber"
                    className="numeric"
                    value={form.identificationNumber}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        // Normalised as typed, so a member pasting a spaced or
                        // hyphenated number is not told it is the wrong shape.
                        identificationNumber: normaliseIdentificationNumber(
                          current.identificationType,
                          e.target.value,
                        ),
                      }))
                    }
                  />
                </Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <Field label="Residential address" htmlFor="address" required error={fieldErrors['residentialAddress']}>
                <Input id="address" value={form.residentialAddress} onChange={set('residentialAddress')} />
              </Field>
              <div className="grid gap-5 sm:grid-cols-3">
                <Field label="City / town" htmlFor="city" required error={fieldErrors['city']}>
                  <Input id="city" value={form.city} onChange={set('city')} />
                </Field>
                <Field label="State" htmlFor="state" required error={fieldErrors['state']}>
                  <Select id="state" value={form.state} onChange={set('state')}>
                    <option value="">Select…</option>
                    {NIGERIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="LGA" htmlFor="lga" required error={fieldErrors['lga']}>
                  <Input id="lga" value={form.lga} onChange={set('lga')} />
                </Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Employment status" htmlFor="employment" required error={fieldErrors['employmentStatus']}>
                <Select id="employment" value={form.employmentStatus} onChange={set('employmentStatus')}>
                  <option value="">Select…</option>
                  {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Organisation" htmlFor="organisation" error={fieldErrors['organisation']}>
                  <Input id="organisation" value={form.organisation} onChange={set('organisation')} />
                </Field>
                <Field label="Job title" htmlFor="jobTitle" error={fieldErrors['jobTitle']}>
                  <Input id="jobTitle" value={form.jobTitle} onChange={set('jobTitle')} />
                </Field>
              </div>
              <Field
                label="Monthly income band"
                htmlFor="income"
                hint="A band, not an exact figure. It helps assess what you can comfortably repay."
              >
                <Select id="income" value={form.monthlyIncomeBand} onChange={set('monthlyIncomeBand')}>
                  <option value="">Prefer not to say</option>
                  {INCOME_BANDS.map((band) => (
                    <option key={band} value={band}>
                      {band}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-foreground-secondary">
                Someone UNIFEMGA can contact about your membership if we cannot reach you.
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" htmlFor="nokName" required error={fieldErrors['nextOfKin.fullName']}>
                  <Input id="nokName" value={form.nokFullName} onChange={set('nokFullName')} />
                </Field>
                <Field label="Relationship" htmlFor="nokRel" required error={fieldErrors['nextOfKin.relationship']}>
                  <Input id="nokRel" placeholder="e.g. Spouse, Brother" value={form.nokRelationship} onChange={set('nokRelationship')} />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Phone number" htmlFor="nokPhone" required error={fieldErrors['nextOfKin.phone']}>
                  <PhoneInput
                    id="nokPhone"
                    value={form.nokPhone}
                    onChange={(v) => setForm((c) => ({ ...c, nokPhone: v }))}
                  />
                </Field>
                <Field label="Email" htmlFor="nokEmail" error={fieldErrors['nextOfKin.email']}>
                  <Input id="nokEmail" type="email" value={form.nokEmail} onChange={set('nokEmail')} />
                </Field>
              </div>
              <Field label="Address" htmlFor="nokAddress" required error={fieldErrors['nextOfKin.address']}>
                <Input id="nokAddress" value={form.nokAddress} onChange={set('nokAddress')} />
              </Field>
            </>
          )}

          {step === 4 && (
            <>
              <Alert tone="info">
                This is the account a loan would be paid into. It must be in your own name.
              </Alert>
              <Field label="Bank" htmlFor="bank" required error={fieldErrors['bankDetails.bankName']}>
                <Select id="bank" value={form.bankName} onChange={set('bankName')}>
                  <option value="">Select…</option>
                  <optgroup label="Non-interest banks">
                    {BANKS.filter((b) => (ISLAMIC_BANKS as readonly string[]).includes(b.name)).map((bank) => (
                      <option key={bank.code} value={bank.name}>
                        {bank.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="All banks">
                    {BANKS.filter((b) => !(ISLAMIC_BANKS as readonly string[]).includes(b.name)).map((bank) => (
                      <option key={bank.code} value={bank.name}>
                        {bank.name}
                      </option>
                    ))}
                  </optgroup>
                </Select>
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label="Account number"
                  htmlFor="accountNumber"
                  required
                  error={
                    fieldErrors['bankDetails.accountNumber'] ??
                    (validateAccountNumber(form.accountNumber) ?? undefined)
                  }
                  hint="10 digits."
                >
                  <Input
                    id="accountNumber"
                    className="numeric"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.accountNumber}
                    onChange={(e) =>
                      setForm((c) => ({ ...c, accountNumber: e.target.value.replace(/\D/g, '') }))
                    }
                  />
                </Field>
                <Field label="Account name" htmlFor="accountName" required error={fieldErrors['bankDetails.accountName']}>
                  <Input id="accountName" value={form.accountName} onChange={set('accountName')} />
                </Field>
              </div>
              <Field
                label="BVN"
                htmlFor="bvn"
                error={fieldErrors['bankDetails.bvn'] ?? (validateBvn(form.bvn) ?? undefined)}
                hint="Optional. 11 digits. Stored encrypted and never displayed in full."
              >
                <Input
                  id="bvn"
                  className="numeric"
                  inputMode="numeric"
                  maxLength={11}
                  value={form.bvn}
                  onChange={(e) => setForm((c) => ({ ...c, bvn: e.target.value.replace(/\D/g, '') }))}
                />
              </Field>
            </>
          )}

          {step === 5 && <DocumentsStep profile={profile} />}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          <ArrowLeft size={16} /> Back
        </Button>

        <div className="flex gap-3">
          <Button variant="outline" loading={saveDraft.isPending} onClick={() => saveDraft.mutate()}>
            Save draft
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)}>
              Next <ArrowRight size={16} />
            </Button>
          ) : null}
        </div>
      </div>

      {step === STEPS.length - 1 && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            {profile.missingDocuments.length > 0 && (
              <Alert tone="warning" className="mb-4">
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle size={14} />
                  Still needed:{' '}
                  {profile.missingDocuments.map((d) => DOCUMENT_TYPE_LABELS[d] ?? d).join(', ')}
                </span>
              </Alert>
            )}

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
              />
              <span className="text-sm text-foreground-secondary">
                I confirm that the information and documents I have provided are accurate and belong
                to me.
              </span>
            </label>

            <Button
              className="mt-5"
              full
              size="lg"
              loading={submit.isPending}
              disabled={!declaration || profile.missingDocuments.length > 0}
              onClick={() => submit.mutate()}
            >
              Submit for verification
            </Button>

            <p className="mt-3 text-center text-xs text-foreground-secondary">
              Your declaration is recorded with the date and your IP address.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Document upload.
 *
 * The file goes straight from the browser to object storage using a presigned
 * URL — it never passes through the API. The server is told about it only once
 * the upload has actually succeeded.
 */
function DocumentsStep({ profile }: { profile: KycProfile }) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState<string | null>(null);

  const upload = async (documentType: string, file: File) => {
    setUploading(documentType);
    try {
      const presigned = await api.post<{ uploadUrl: string; key: string }>(
        '/kyc/me/documents/upload-url',
        {
          documentType,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        },
      );

      const response = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status}).`);

      await api.post('/kyc/me/documents', {
        documentType,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        storageKey: presigned.key,
      });

      toast.success(`${DOCUMENT_TYPE_LABELS[documentType] ?? documentType} uploaded.`);
      void queryClient.invalidateQueries({ queryKey: ['kyc-me'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Upload failed.');
    } finally {
      setUploading(null);
    }
  };

  const allTypes = [
    ...profile.requiredDocuments,
    'PROOF_OF_ADDRESS',
    'EMPLOYMENT_DOCUMENT',
  ].filter((value, index, array) => array.indexOf(value) === index);

  return (
    <div className="space-y-4">
      <p className="text-sm text-foreground-secondary">
        JPG, PNG, WebP or PDF, up to 10 MB. Documents are stored privately and are only reachable
        through a short-lived link — every time an officer opens one, it is recorded.
      </p>

      {allTypes.map((documentType) => {
        const existing = profile.documents.find((d) => d.documentType === documentType);
        const required = profile.requiredDocuments.includes(documentType);
        const busy = uploading === documentType;

        return (
          <div
            key={documentType}
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4',
              existing ? 'border-success/30 bg-success-soft/30' : 'border-border',
            )}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {DOCUMENT_TYPE_LABELS[documentType] ?? documentType}
                {required && <span className="ml-1 text-danger">*</span>}
              </p>
              {existing ? (
                <p className="truncate text-xs text-foreground-secondary">
                  {existing.fileName} · {(existing.fileSize / 1024).toFixed(0)} KB
                </p>
              ) : (
                <p className="text-xs text-foreground-secondary">
                  {required ? 'Required' : 'Optional'}
                </p>
              )}
              {existing?.rejectionReason && (
                <p className="mt-1 text-xs font-medium text-danger">{existing.rejectionReason}</p>
              )}
            </div>

            <label className="shrink-0">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload(documentType, file);
                  e.target.value = '';
                }}
              />
              <span
                className={cn(
                  'inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-input px-4 text-sm font-semibold transition-colors hover:bg-accent',
                  busy && 'pointer-events-none opacity-50',
                )}
              >
                {busy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : existing ? (
                  <Trash2 size={16} />
                ) : (
                  <FileUp size={16} />
                )}
                {busy ? 'Uploading…' : existing ? 'Replace' : 'Upload'}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

const ID_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  IDENTIFICATION_TYPES.map((type) => [type.value, type.label]),
);

/**
 * The record as held, grouped the way it was asked for.
 *
 * A flat list of nine fields gave equal weight to a legal name and a document
 * count, and read as a column of small grey headings. The groups are the same
 * ones the form uses, so a member looking for what they typed finds it where
 * they typed it.
 */
function SummaryCard({ profile }: { profile: KycProfile }) {
  const kin = profile.nextOfKin;
  const bank = profile.bankDetails;

  return (
    <div className="space-y-4">
      <Section title="Identity">
        <Item label="Full legal name" value={profile.fullLegalName} />
        <Item
          label="Date of birth"
          value={profile.dateOfBirth ? formatLongDate(new Date(profile.dateOfBirth)) : null}
        />
        <Item label="Gender" value={profile.gender ? humanise(profile.gender) : null} />
        <Item
          label={
            (profile.identificationType && ID_TYPE_LABELS[profile.identificationType]) ??
            'Identification'
          }
          value={profile.identificationNumber}
        />
      </Section>

      <Section title="Address">
        <Item label="Residential address" value={profile.residentialAddress} wide />
        <Item label="City" value={profile.city} />
        <Item
          label="State and LGA"
          value={[profile.state, profile.lga].filter(Boolean).join(' · ') || null}
        />
      </Section>

      <Section title="Employment">
        <Item
          label="Status"
          value={
            profile.employmentStatus
              ? EMPLOYMENT_STATUS_LABELS[profile.employmentStatus]
              : null
          }
        />
        <Item label="Organisation" value={profile.organisation} />
        <Item label="Job title" value={profile.jobTitle} />
        <Item label="Monthly income" value={profile.monthlyIncomeBand} />
      </Section>

      <Section title="Next of kin">
        <Item label="Name" value={kin?.fullName} />
        <Item label="Relationship" value={kin?.relationship} />
        <Item label="Phone" value={kin?.phone} />
        <Item label="Email" value={kin?.email} />
        <Item label="Address" value={kin?.address} wide />
      </Section>

      <Section title="Bank account">
        <Item label="Bank" value={bank?.bankName} />
        <Item label="Account name" value={bank?.accountName} />
        <Item label="Account number" value={bank?.accountNumber} />
      </Section>

      <Section title="Documents">
        {profile.documents.length === 0 ? (
          <p className="text-sm text-foreground-secondary sm:col-span-2">
            No documents were uploaded with this record.
          </p>
        ) : (
          profile.documents.map((document) => (
            <div
              key={document.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border px-3.5 py-2.5 sm:col-span-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {DOCUMENT_TYPE_LABELS[document.documentType] ?? document.documentType}
                </p>
                <p className="truncate text-xs text-foreground-secondary">{document.fileName}</p>
              </div>
              <StatusBadge map={KYC_STATUS} status={document.status} />
            </div>
          ))
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-x-6 gap-y-4 sm:grid-cols-2">{children}</CardContent>
    </Card>
  );
}

function Item({
  label,
  value,
  wide,
}: {
  label: string;
  value: string | null | undefined;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <p className="text-xs text-foreground-secondary">{label}</p>
      <p className="mt-0.5 text-sm leading-relaxed text-foreground">{value || '—'}</p>
    </div>
  );
}

function humanise(value: string): string {
  const text = value.replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Whether a step's required fields are actually filled.
 *
 * Position is not progress: walking past a step with Next left it marked green
 * with a tick, which told a member their identity details were done when the
 * fields were empty. The document step has no required field, so it is never
 * claimed as complete here.
 */
const REQUIRED_BY_STEP: ReadonlyArray<ReadonlyArray<string>> = [
  ['fullLegalName', 'dateOfBirth', 'gender', 'identificationType', 'identificationNumber'],
  ['residentialAddress', 'city', 'state', 'lga'],
  ['employmentStatus'],
  ['nokFullName', 'nokRelationship', 'nokPhone'],
  ['bankName', 'accountNumber', 'accountName'],
  [],
];

function isStepComplete(index: number, form: Record<string, string>): boolean {
  const required = REQUIRED_BY_STEP[index];
  if (!required || required.length === 0) return false;
  return required.every((field) => (form[field] ?? '').trim().length > 0);
}

/** Maps a server field path back to the step that contains it. */
function stepForField(field: string): number | null {
  if (/fullLegalName|dateOfBirth|gender|identification/i.test(field)) return 0;
  if (/residentialAddress|city|state|lga/i.test(field)) return 1;
  if (/employment|organisation|jobTitle|income/i.test(field)) return 2;
  if (/nextOfKin/i.test(field)) return 3;
  if (/bankDetails/i.test(field)) return 4;
  return null;
}
